import classnames from 'classnames'
import { CSSProperties, useRef } from 'react'

import { useQuinceNprogressRouter as useRouter } from '@src/modules/navigation'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import Link from '@src/quince-site-common/src/domains/Link'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import checkIsInFirstFold from '@src/quince-site-common/src/utils/checkIsInFirstFold'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'
import { navigateToB2BSelfServe } from '@src/utils/navigate-to-b2b'

import { RichText, RichTextProps } from '../../molecules'
import { preserveParamsOnHashNav } from '../../utils/preserve-params-on-hash-nav'
import { default as Button, ButtonProps } from '../button'
import { Image, ImageProps } from '../image'
import Video, { VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE, VideoProps } from '../video'

import styles from './contentWithBackgroundImage.module.scss'

export interface ContentWithBackgroundImageProps {
  imageProps?: ImageProps
  videoProps?: VideoProps
  titleRichTextProps?: RichTextProps
  descriptionRichTextProps?: RichTextProps
  richTextsItems?: RichTextProps[]
  buttonPropsList?: ButtonProps[]
  extraButtons?: ButtonProps[]
  hasButton?: boolean
  className?: string
  contentClassName?: string
  contentInnerWrapper?: string
  buttonsWrapperClassName?: string
  style?: CSSProperties
  layoutType?: LAYOUT_TYPES
  tracker?: string
  link?: Link
  /** When true, a whole-image click routes via navigateToB2BSelfServe using the container link URL. */
  isContainerB2BSelfServe?: boolean
  styleObject?: CSSProperties
  overrideMaxImageWidth1600?: boolean
  trackingMetaData?: TrackingMetaData
  shouldShowOptimisedTheVideo?: boolean
  fireImageClickEventAlways?: boolean
}

export const ContentWithBackgroundImage = (props: ContentWithBackgroundImageProps) => {
  const { layoutType = LAYOUT_TYPES.DEFAULT, trackingMetaData, className = '', hasButton = false } = props
  const router = useRouter()
  const isWebView = getIsNativeViewClient()

  const wrapperClasses = classnames(
    styles.contentWithBgImageContainer,
    styles.bannerWrap,
    props.link?.navigationPath && styles.bannerWrap__clickable,
    styles.bannerImage,
    className,
    'position--relative',
    styles[`bannerWrap__${layoutType}`],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const deviceInfo = useDeviceInfo()

  const handleNavigation = (navigationPath: string, openInNewTab?: boolean | null) => {
    const isAbsoluteUrl = /^https?:\/\//i.test(navigationPath)
    let isCrossOrigin = false
    // For same-origin absolute URLs, parse to a relative path so router.push works.
    // Use URL constructor (not startsWith) to avoid subdomain/port prefix confusion
    // e.g. https://www.quince.com.evil.com or http://localhost:30001 vs :3000.
    let internalPath = navigationPath
    if (isAbsoluteUrl) {
      try {
        const url = new URL(navigationPath)
        isCrossOrigin = url.origin !== window.location.origin
        internalPath = `${url.pathname}${url.search}${url.hash}` || '/'
      } catch {
        // Malformed URL → treat as cross-origin so we hand it to window.open as-is.
        isCrossOrigin = true
      }
    }

    if (!isCrossOrigin && props.link?.preserveQueryParams) {
      internalPath = preserveParamsOnHashNav(internalPath)
    }

    if (openInNewTab === true) {
      window.open(navigationPath, '_blank', 'noopener,noreferrer')
      return
    }
    if (isCrossOrigin) {
      window.open(navigationPath, '_self')
      return
    }
    router.push(internalPath)
  }

  const handleImageClick = () => {
    if (props.link?.navigationPath) {
      if (!props.buttonPropsList?.length || props.fireImageClickEventAlways) {
        // Note [srimaraj :4apr2025] : Lint bypass to unblock build since i have limited context on this and its impact if isInFirstFold set to false
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const isInFirstFold = containerRef.current ? checkIsInFirstFold(containerRef.current) : false
        // only send this event if Image doesn't have any UI button
        setFindingMethod[FINDING_METHOD_AGGREGATE.CAMPAIGN]?.({
          elementName: trackingMetaData?.context?.contentContainerName ?? '',
          elementId: trackingMetaData?.context?.trackingId ?? '',
        })
        trackClickEvent(TELEMETRY_EVENT_NAMES.LINK_CLICK, {
          trackInternal: true,
          trackGA: true,
          // @i18n-ignore
          action: 'link_click',
          // @i18n-ignore
          category: 'navigation',
          value: props.link.navigationPath,
          label: `${props.tracker}__contentWithBackgroundImage`,
          context: {
            ...trackingMetaData?.context,
            isInFirstFold: isInFirstFold,
          },
        })
      }
      if (props.isContainerB2BSelfServe) {
        navigateToB2BSelfServe(props.link.url ?? props.link.navigationPath)
        return
      }
      const { navigationPath, openInNewTab } = props.link
      if (isWebView) {
        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              // @i18n-ignore
              action: 'NAVIGATION',
              url: window.location.href,
              newURL: navigationPath.split('?')[0],
              nextURL: navigationPath,
              openInNewTab,
            }),
          )
        } catch (error) {
          // postMessage failed — fall back to in-page navigation.
          handleNavigation(navigationPath, openInNewTab)
          console.error('Error in sending message to native webview ', error)
        }
      } else {
        handleNavigation(navigationPath, openInNewTab)
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className={wrapperClasses}
      style={{ ...props.style, ...props.styleObject }}
      onClick={() => {
        handleImageClick()
      }}
    >
      {props.videoProps?.url && (
        <Video
          {...props.videoProps}
          muted
          autoplay
          loop
          controls={false}
          loadingType={VIDEO_LOADING_TYPE.Lazy}
          preload={VIDEO_PRELOAD_TYPE.Auto}
          posterImage={props.imageProps}
          className={classnames(styles.bannerMedia, props.videoProps.className)}
        />
      )}
      {props.imageProps?.url && !props.videoProps?.url && (
        <Image
          alt={props.imageProps.alt}
          {...props.imageProps}
          className={classnames(styles.bannerMedia, props.imageProps.className)}
          overrideMaxImageWidth1600={props.overrideMaxImageWidth1600}
          maxWidth={getMaxWidthInPx({
            maxWidthDesktopInRem: styles.backgroundImageWidthDesktop,
            maxWidthMobileInRem: styles.backgroundImageWidthMobile,
            deviceType: deviceInfo.deviceType,
          })}
        />
      )}
      <div className={classnames(styles.content, props.contentClassName)}>
        <div className={classnames(styles.contentInnerWrapper, props.contentInnerWrapper)}>
          {props.richTextsItems?.map((richTextItem, idx) => (
            <RichText key={idx} {...richTextItem} />
          ))}
          {((hasButton && props.buttonPropsList?.length) ?? props.extraButtons?.length) ? (
            <div className={classnames(styles.buttonWrapper, props.buttonsWrapperClassName)}>
              {props.buttonPropsList?.map((buttonProps: ButtonProps) => {
                return (
                  <Button
                    {...buttonProps}
                    key={buttonProps.id}
                    className={classnames(styles.actionButton, buttonProps.className)}
                    trackConfig={
                      props.tracker
                        ? {
                            trackInternal: true,
                            trackGA: true,
                            // @i18n-ignore
                            action: 'button_click',
                            // @i18n-ignore
                            category: 'navigation',
                            value: `${buttonProps.href}`,
                            label: `${props.tracker}__contentWithBackgroundImage__button`,
                            context: trackingMetaData?.context,
                          }
                        : undefined
                    }
                  />
                )
              })}
              {props.extraButtons?.map((buttonProps: ButtonProps) => (
                <Button
                  {...buttonProps}
                  key={buttonProps.id}
                  className={classnames(styles.actionButton, buttonProps.className)}
                  trackConfig={
                    props.tracker
                      ? {
                          trackInternal: true,
                          trackGA: true,
                          // @i18n-ignore
                          action: 'button_click',
                          // @i18n-ignore
                          category: 'navigation',
                          value: `${buttonProps.href ?? buttonProps.id}`,
                          label: `${props.tracker}__contentWithBackgroundImage__button`,
                          context: trackingMetaData?.context,
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ContentWithBackgroundImage
